import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { UserDetails } from '../../types/user-details.type';
import { UserSelectionsService } from '../../services/user-selections/user-selections.service';
import { AppSettings } from '../../PRE-LOAD/app-settings';
import { StringsService } from '../../services/strings/strings.service';
import { pairwise, startWith } from 'rxjs/operators';
import { MarketingPreferencesComponent } from 'bmwg-angular-library/lib/marketing-preferences/marketing-preferences.component';
import { SelectedMarketingOptions } from 'bmwg-angular-library/lib/marketing-preferences/marketing-preferences-types';
import { Product, ProductVariant } from '../../types/product.type';
import { AnalyticsService } from '../../services/analytics/analytics.service';

@Component({
    selector: 'rttd-details-form',
    templateUrl: './details-form.component.html',
    styles: [
        require('./details-form.component.scss').default.toString(),
        require(`./details-form.component.${environment.brand}.scss`).default.toString()
    ]
})
export class DetailsFormComponent implements OnInit {
    @Output() isValid = new EventEmitter<boolean>();
    @Output() validationError = new EventEmitter<any>();

    content: UserDetails;
    preferences: MarketingPreferencesComponent;

    // Form group for the details form.
    detailsForm: FormGroup;

    marketingPreferences: SelectedMarketingOptions;

    brand = environment.brand.toUpperCase();
    errorsStrings = {
        email: {
            required: 'Please enter your email address',
            pattern: 'Please enter a valid email address'
        },
        phoneNumber: {
            required: 'Please enter your phone number',
            pattern: 'Please enter a valid phone number'
        }
    };

    links = {
        retailerTermsAndConditions: this.appSettings.retailerTermsAndConditions
    };


    // * Autocomplete =>
    // See comments in pickup-location.component.ts
    forcedInputs = {
        firstname: false,
        lastname: false,
        emailAddress: false,
        phoneNumber: false,
        seatsPerBooking: false
    };

    guests = [];
    eventSeatsPerBooking:number = 1;
    variants: ProductVariant[];

    constructor(private userSelectionsService: UserSelectionsService, private appSettings: AppSettings, private stringsService: StringsService, private analytics: AnalyticsService) {

    }

    ngOnInit() {

        this.userSelectionsService.currentProductSelections$.subscribe((product: Product) => {
            this.eventSeatsPerBooking = product.seatsperbooking ? parseInt(product.seatsperbooking) : 1;
            this.variants = product.variants;
        });

        var maxSeats: number = + this.eventSeatsPerBooking;
        for (let i = 1; i < maxSeats; i++) {
            this.guests.push({ number: i, guestplusbooker: i + 1 });
        }

        this.stringsService.get().subscribe((data: UserDetails) => {
            this.content = data['UserDetails'];
        });

        let ukPhoneRegex = '^(((\\+44\\s?\\d{4}|\\(?0\\d{4}\\)?)\\s?\\d{3}\\s?\\d{3})|((\\+44\\s?\\d{3}|\\(?0\\d{3}\\)?)\\s?\\d{3}\\s?\\d{4})|((\\+44\\s?\\d{2}|\\(?0\\d{2}\\)?)\\s?\\d{4}\\s?\\d{4}))(\\s?\\#(\\d{4}|\\d{3}))?$';
        let irePhoneRegex = '^(((\\+353\\s?\\d{1}|\\(?0\\d{1}\\)?)\\s?\\d{3}\\s?\\d{4})|((\\+353\\s?\\d{2}|\\(?0\\d{2}\\)?)\\s?\\d{3}\\s?\\d{4})|((\\+353\\s?\\d{2}|\\(?0\\d{2}\\)?)\\s?\\d{3}\\s?\\d{3})|((\\+353\\s?\\d{2}|\\(?0\\d{2}\\)?)\\s?\\d{2})|((\\+353\\s?\\d{3}|\\(?0\\d{3}\\)?)\\s?\\d{2})|((\\+353\\s?\\d{3}|\\(?0\\d{3}\\)?)\\s?\\d{3}\\s?\\d{3}))(\\s?\\#(\\d{4}|\\d{3}))?$';

        let phoneNumberValidators = [
            Validators.required,
            Validators.maxLength(18),
            Validators.pattern(this.appSettings.externalReferenceId.includes("ireland") ? irePhoneRegex : ukPhoneRegex)
        ]

        let preferredPerformanceValidators = [
             Validators.required,
             Validators.minLength(1)
        ]

        let phoneRegex = this.appSettings.externalReferenceId.includes("ireland") ? irePhoneRegex : ukPhoneRegex;
        this.detailsForm = new FormGroup({
            firstname: new FormControl(this.bruteForceInitialValue('firstname'), [
                Validators.required,
                Validators.maxLength(60)
            ]),
            lastname: new FormControl(this.bruteForceInitialValue('lastname'), [
                Validators.required,
                Validators.maxLength(75)
            ]),
            emailAddress: new FormControl(this.bruteForceInitialValue('emailAddress'), [
                Validators.required,
                Validators.maxLength(100),
                Validators.email,
                Validators.pattern('^[_A-Za-z0-9-\\+]+(\\.[_A-Za-z0-9-]+)*@[A-Za-z0-9-]+(\\.[A-Za-z0-9-]+)*(\\.[A-Za-z]{2,})$')
            ]),
            phoneNumber: new FormControl(this.bruteForceInitialValue('phoneNumber'), phoneNumberValidators),
            seatsPerBooking: new FormControl('1', {
                updateOn: 'change'
            }),
            productVariant: new FormControl('', {
                validators: this.appSettings.eventJourney || this.variants == null ? undefined : preferredPerformanceValidators,
                updateOn: 'change'
            },),
        }, { 
            updateOn: 'blur' });

        // * Autocomplete.
        [
            'firstname',
            'lastname',
            'emailAddress',
            'phoneNumber',
            'seatsPerBooking',
            'productVariant'
        ].forEach((type) => {
            this.detailsForm.get(type).valueChanges
                .pipe(startWith(this.bruteForceInitialValue(type)), pairwise())
                .subscribe(([prev, next]: [any, any]) => {
                    this.bruteForceAutoCompleteValue(type, next);
                    this.forcedInputs[type] = false;
                });
        });
    }

    // * Autocomplete.
    bruteForceAutoCompleteValue(type, value) {
        if (!this.forcedInputs[type]) {
            this.forcedInputs[type] = true;
            this.detailsForm.patchValue({ [type]: value });
        }
        const userDetails: UserDetails = {
            firstname: this.detailsForm.controls.firstname.value,
            lastname: this.detailsForm.controls.lastname.value,
            emailAddress: this.detailsForm.controls.emailAddress.value,
            phoneNumber: this.detailsForm.controls.phoneNumber.value,
            seatsPerBooking: this.detailsForm.controls.seatsPerBooking.value,
            productVariant: this.detailsForm.controls.productVariant.value,
            
        };

        this.userSelectionsService.set('userDetails', userDetails);
    }

    // * Autocomplete.
    bruteForceInitialValue(type) {
        if (
            this.userSelectionsService.get('userDetails') &&
            this.userSelectionsService.get('userDetails')[type]) {
            return this.userSelectionsService.get('userDetails')[type];
        } else {
            return '';
        }
    }

    inputFocusHasFocus() {
    }

    /**
     * @description Attempt to submit the userDetails form. If the form is valid, then push the details
     * to the userSelectionService and emit true. If invalid, set all form elements to dirty which will
     * present any error messages.
     * @return boolean
     */
    public onSubmit() {
        if (this.detailsForm.valid) {
            this.isValid.emit(true);
            return true;
        } else {
            // The form has attempted to be submitted, but the form contains
            // at least one invalid field input. Go through each form control
            // and if it contains an invalid input, mark it as dirty.

            let validationResult:Array<any> = [];
            let isError = false;
            this.isValid.emit(false);
            Object.keys(this.detailsForm.controls).forEach(key => {
                let field = this.detailsForm.controls[key];
                field.markAsDirty();
                var error = field.errors;

                if (error != null){
                    isError = true;
                }

                    validationResult.push({
                           name: key,
                           valid: error != null ? false : true,
                           completed: field.value.length > 0 ? true : false,
                           invalidReason: error != null ? Object.keys(error) : null
                       });
                
            });

            if (isError){
                this.validationError.emit(validationResult);
            }
              
            return false;
        }
    }
}
